// PricingCalculator — interactive what-if pricing tool + quote builder.
//
// Accessible to ALL users (back office + owner). The office manager can
// pick a customer, select SKUs + quantities, and see the live-computed
// price from the pure pricing engine on the server.
//
// Flow:
//   1. Pick customer (dropdown) — hidden when jobId prop is set
//   2. Enter address + optional email — hidden when jobId prop is set
//   3. Add line items: [SKU dropdown] + [quantity input] + [remove button]
//   4. Pricing recomputes AUTOMATICALLY (debounced POST /api/pricing/preview) on
//      every add/remove/edit — there is no Calculate button or auto-calc toggle.
//   5. See the Item rows (Cost/Sell/Margin) + the Profitability summary.
//   6. Footer: "Save quote" (persists) + "Cancel" (closes the modal, discards draft).
//
// Auto-recalc + "Clear all" are DRAFT/display only — they never persist; the only
// write is "Save quote". The preview endpoint is read-only.
//
// Embedded in the JobDetailPage "Edit estimate" dialog (jobId set). Stateless
// except for its internal form state; remounts (re-seeds) each time the dialog opens.

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Calculator, Send, CheckCircle2 } from 'lucide-react';
import { getCustomers, getInventorySkus, getSkuCategories, postPricingPreview, postQuoteCreate, clearQuote, getNextPo, getJobDetail, postMiscLine, deleteMiscLine } from '../api/client.js';
import {
  bagsFromSf,
  CANONICAL_UOMS,
  type PricingResult,
  type CanonicalUom,
  type MiscLineCreateInput,
} from '@gutterinv/shared';
import type { QuoteCreateResponse } from '../api/client.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.js';
import { Button } from '../components/ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog.js';
import { Input } from '../components/ui/input.js';
import { SkuSearchSelect } from './SkuSearchSelect.js';
import { presetsForSkuCode, encodePresetKey, buildPricingRequestLines } from '../lib/sku-line-presets.js';
import { isBattingCategory } from '../lib/product-line.js';
import { CustomerSearchSelect } from './CustomerSearchSelect.js';
import {
  formatCents,
  formatMarginPct,
} from '../lib/format.js';

interface LineItem {
  key: number;
  skuId: string;
  quantity: string;
  /** Per-line R-value, preserved across the edit round-trip so a re-quote keeps the
   *  customer-PDF "— R-XX" suffix. Preset lines carry R in the composite skuId (and
   *  toRequestLineItems decodes it there); this field is the source for NON-preset
   *  batts (e.g. B09E) seeded from saved data. Cleared when the row's SKU changes. */
  rValue?: string | null;
}

export interface PricingCalculatorInitialLineItem {
  skuId: string;
  /** Stored as string to match the form's input model — calculator
   *  treats line items as string-form-state until pricing engine call. */
  quantity: string;
  /** Seeded per-line R-value (from the saved field estimate) — preserved on re-quote. */
  rValue?: string | null;
}

/** A free-form "Miscellaneous" quote line as the calculator's form state holds it.
 *  `id` is set only for PERSISTED lines (seeded from job.miscLines); a new draft row
 *  has no id until it's saved. `amount` is dollars-as-string (form input model);
 *  the flat line total in cents is derived on save/preview. */
interface MiscLineDraft {
  key: number;
  /** Server id for a persisted line; undefined for an unsaved draft row. */
  id?: string;
  description: string;
  /** Optional canonical UOM (informational) or '' for none. */
  uom: CanonicalUom | '';
  /** Optional informational quantity (string form input) or ''. */
  quantity: string;
  /** Flat line total, dollars-as-string. */
  amount: string;
}

/** Seed shape for existing persisted misc lines (from job.miscLines), passed by the
 *  parent so a re-open round-trips them + the two-phase save can diff by id. */
export interface PricingCalculatorInitialMiscLine {
  id: string;
  description: string;
  uom: string | null;
  quantity: number | null;
  amountCents: number;
}

export interface PricingCalculatorProps {
  /** When set, binds to an existing job (hides customer/address fields). */
  jobId?: string;
  /** Pre-set customer ID (used when jobId is set so the dropdown is hidden). */
  initialCustomerId?: string;
  /**
   * Seed the line items list from existing data — typically the job's
   * confirmed field_estimates from the crew's /estimate submission, OR
   * the prior quote's saved line items on a re-quote. Office can edit,
   * remove, or add. If empty/undefined the calculator opens with a
   * single blank row (the "build from scratch" / template path the
   * office uses to bypass the crew estimate).
   */
  initialLineItems?: PricingCalculatorInitialLineItem[];
  /**
   * Seed the free-form Miscellaneous lines from the job's persisted misc
   * (job.miscLines). Carries the server id so a re-open round-trips them and
   * the two-phase save can diff current-vs-desired by id (add new / delete
   * removed) without a blind clear+re-add. Undefined/empty → no misc rows.
   */
  initialMiscLines?: PricingCalculatorInitialMiscLine[];
  /** Called after a successful quote create/save so the parent can refetch. */
  onQuoteSaved?: (res: QuoteCreateResponse) => void;
  /** Closes the host modal (the footer "Cancel" button) discarding unsaved draft
   *  changes. When omitted (non-modal contexts), the Cancel button is not shown. */
  onCancel?: () => void;
  /** Read-only "View Profitability" mode (job at Quoted or later). Hides the line-
   *  item editor + Save/Clear, renders the COST/SELL/MARGIN table + Profitability
   *  from the FROZEN quote snapshot (never the live preview), Job Sell as static
   *  text. Mode is driven by the job's status by the caller. */
  readOnly?: boolean;
}

let nextKey = 1;

// ONE shared column grid for the whole calculator readout (line items, subtotal,
// and the Profitability rows) so every COST / SELL / MARGIN value right-aligns on a
// single common edge top-to-bottom. Tracks: [info | COST | SELL | MARGIN].
const READOUT_GRID = 'grid grid-cols-[minmax(0,1fr)_5.5rem_7rem_4rem] gap-x-3';

export function PricingCalculator({
  jobId,
  initialCustomerId,
  initialLineItems,
  initialMiscLines,
  onQuoteSaved,
  onCancel,
  readOnly = false,
}: PricingCalculatorProps = {}) {
  const queryClient = useQueryClient();

  // This app is tax-free (sales tax removed): no tax is computed or displayed
  // anywhere. Job total = materials + labor charge.

  const { data: customers } = useQuery({
    queryKey: ['customers'],
    queryFn: getCustomers,
  });
  const { data: inventoryData } = useQuery({
    queryKey: ['inventory', 'skus'],
    queryFn: () => getInventorySkus(),
  });
  // Categories (ordered by displayOrder) feed the SKU picker's cascade filter.
  const { data: categoriesRaw } = useQuery({
    queryKey: ['sku-categories'],
    queryFn: getSkuCategories,
  });
  const categories = useMemo(
    () =>
      [...(categoriesRaw ?? [])].sort(
        (a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name),
      ),
    [categoriesRaw],
  );
  // Defensive: the picker only ever offers ACTIVE SKUs. The endpoint already
  // returns active-only, but this guards against any future caller repopulating
  // the shared cache with disabled rows. Already-selected historical inactive
  // lines still resolve via SkuSearchSelect's "Unavailable SKU" fallback (keyed
  // on the selected value, not this candidate list).
  // ...and NEVER parent SKUs (abstract coil profiles like C113427 — only their
  // color children are real, quotable items). Server rejects a parent line too.
  // Replace any shared-bag SKU (e.g. KN3646) with its R-value preset OPTIONS, so the
  // bare bag isn't a sellable estimate line. Each preset is a synthetic row with a
  // COMPOSITE skuId (`<realId>::R-30`, decoded at submit) carrying the preset coverage,
  // so it behaves as a normal batting line (SF input → bags) end to end. Estimate-
  // picker-only — the real KN3646 stays untouched for receiving/inventory/reorder.
  const skus = useMemo(
    () =>
      (inventoryData?.skus ?? [])
        .filter((s) => s.active && !s.isParent)
        .flatMap((s) => {
          const presets = presetsForSkuCode(s.code);
          if (presets.length === 0) return [s];
          return presets.map((p) => ({
            ...s,
            skuId: encodePresetKey(s.skuId, p.rValue),
            name: `${s.name} — ${p.rValue}`,
            coverageSf: p.coverageSf,
          }));
        }),
    [inventoryData],
  );
  // Insulation batting SKUs carry coverage_sf (SF per bag). For those lines the
  // office enters actual SF; we send `enteredSf` so the server prices whole bags
  // (ceil) for materials but charges labor on the real SF. Keyed off the SKU's
  // coverage_sf alone — only batting has it, so no job-product-line lookup needed.
  const skuById = useMemo(() => {
    const m = new Map<string, (typeof skus)[number]>();
    for (const s of skus) m.set(s.skuId, s);
    return m;
  }, [skus]);
  const lineCoverageSf = (skuId: string): number | null =>
    skuById.get(skuId)?.coverageSf ?? null;
  // Insulation (batting) lines are bought/quoted in whole BAGS but carry
  // consumptionUom='SF' (for Inventory display) and sometimes a stale unit — so the
  // cost/sell table must label EVERY insulation line by its PURCHASE unit (BAG), not
  // consumptionUom ("SF") or the base unit ("ea"). Map = batting-category SKU code →
  // purchaseUom. A line resolved here is insulation → label BAG; else fall through to
  // the normal consumptionUom||unit. (Active-only inventory: a historical line whose
  // SKU is now inactive won't resolve → labels via the else-branch; cosmetic only.)
  const insulationUnitByCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of inventoryData?.skus ?? []) {
      if (s.purchaseUom && isBattingCategory(s.categoryName)) m.set(s.code, s.purchaseUom);
    }
    return m;
  }, [inventoryData]);
  // Thin wrapper: the pure builder lives in sku-line-presets (unit-tested). It reads
  // coverage via the CURRENT skuById — so any memoized caller (doCalculate) MUST list
  // skuById in its deps, or it captures a stale empty map and drops enteredSf/coverage.
  const toRequestLineItems = (
    items: { skuId: string; quantity: string; rValue?: string | null }[],
  ) => buildPricingRequestLines(items, lineCoverageSf);

  const { data: nextPoData } = useQuery({
    queryKey: ['next-po'],
    queryFn: getNextPo,
    enabled: !jobId,
  });

  // When bound to a job, fetch it for the LABOR side of the margin summary.
  // The server resolves footage + the live/frozen rate AND cost rate (status-
  // driven) — never recompute footage client-side. Create-mode (no jobId) has
  // no labor, so the Labor block is hidden there.
  const { data: job } = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => getJobDetail(jobId!),
    enabled: !!jobId,
  });

  const [customerId, setCustomerId] = useState(initialCustomerId ?? '');
  const [address, setAddress] = useState('');
  // PO stays empty by default — server auto-assigns and atomically
  // increments tenants.next_po_number on insert. nextPoData is only
  // used to show what the auto-assigned number WILL be (placeholder).
  // Pre-filling the value defeated the auto-increment by always
  // sending the same "next" number back as if the user typed it.
  const [poNumber, setPoNumber] = useState('');
  // Seed line items from initialLineItems when provided (typically the
  // crew's BoM or the prior quote's saved items on a re-quote). Falls
  // back to a single blank row when empty/absent — that's the "build
  // from scratch" path used for office bypass and template-package
  // workflows. Lazy initializer runs once per mount; remounting (e.g.
  // closing & re-opening the dialog) re-seeds from the latest props.
  const [lineItems, setLineItems] = useState<LineItem[]>(() => {
    if (initialLineItems && initialLineItems.length > 0) {
      return initialLineItems.map((li) => ({
        key: nextKey++,
        skuId: li.skuId,
        quantity: li.quantity,
        rValue: li.rValue ?? null,
      }));
    }
    return [{ key: nextKey++, skuId: '', quantity: '' }];
  });
  // Track whether items came from a seed so we can show a small hint to
  // the office that says "Pre-filled — review and adjust". Seeded state
  // resets when user empties the list (no more hint after that).
  const [wasSeeded, setWasSeeded] = useState(
    Boolean(initialLineItems && initialLineItems.length > 0),
  );
  // Free-form Miscellaneous lines (migration 0075) — a SEPARATE list from the SKU
  // lineItems so the existing SKU row editor / validItems / quotes-create save path
  // stay untouched (structural mirror of the separate job_misc_lines table). Seeded
  // (WITH server ids) from the job's persisted misc so a re-open round-trips them and
  // the two-phase save can diff current-vs-desired by id. Lazy init runs once per mount.
  const [miscLines, setMiscLines] = useState<MiscLineDraft[]>(() =>
    (initialMiscLines ?? []).map((m) => ({
      key: nextKey++,
      id: m.id,
      description: m.description,
      uom: (m.uom ?? '') as CanonicalUom | '',
      quantity: m.quantity != null ? String(m.quantity) : '',
      amount: (m.amountCents / 100).toFixed(2),
    })),
  );
  // True while the two-phase save's misc reconcile (POST/DELETE) is in flight, so the
  // footer Save button stays disabled through BOTH phases (SKU create + misc reconcile).
  const [miscSaving, setMiscSaving] = useState(false);
  // Key of a row the user just added via "Add item" — only that row's SKU picker
  // auto-opens (on its mount). Null on modal open so nothing auto-opens then,
  // keeping existing rows safe from focus-stealing / accidental overwrite. Keys
  // are monotonic + never reused and rows never remount, so a stale value here
  // can never reopen an old row.
  const [autoOpenKey, setAutoOpenKey] = useState<number | null>(null);
  const [result, setResult] = useState<PricingResult | null>(null);
  const [successMsg, setSuccessMsg] = useState('');
  // "Clear all" confirmation — clears the draft rows only after the user confirms.
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  // Quote-amount override: lets the office override the line-item-computed
  // total without touching the BoM. Empty string = "no override, use the
  // computed total". `overrideTouched` tracks whether the user has manually
  // edited the input — once they have, we stop auto-syncing it from the
  // computed total so their override sticks across recalculations.
  const [overrideDollars, setOverrideDollars] = useState('');
  const [overrideTouched, setOverrideTouched] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const pricingMutation = useMutation({
    mutationFn: postPricingPreview,
    onSuccess: (data) => setResult(data),
    onError: () => setResult(null),
  });

  // SKU-save mutation. The post-save finalize (toast + form reset + query
  // invalidation + onQuoteSaved) is NOT in onSuccess anymore — it runs in
  // handleCreate AFTER the misc reconcile (phase 2) so the dialog doesn't close
  // before misc persists. See finalizeAfterSave + reconcileMisc below.
  const createMutation = useMutation({
    mutationFn: postQuoteCreate,
  });

  // "Save quote" on an EMPTY draft (after Clear all) persists the wipe: deletes the
  // saved quote line items + resets the amount to $0 server-side, then closes.
  const clearMutation = useMutation({
    mutationFn: () => clearQuote(jobId as string),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard', 'today'] });
      if (jobId) {
        void queryClient.invalidateQueries({ queryKey: ['job', jobId] });
        void queryClient.invalidateQueries({ queryKey: ['job-phases', jobId] });
      }
      onCancel?.(); // close the modal; the parent refetches the now-$0 job
    },
  });

  // Valid line items (have both skuId and a positive quantity)
  const validItems = lineItems.filter(
    (li) => li.skuId && li.quantity && Number(li.quantity) > 0,
  );
  // Misc lines valid for preview/persistence: a non-empty description + a finite,
  // non-negative dollar amount. uom/quantity are optional + informational only.
  const dollarsToCents = (s: string): number => Math.round(Number(s) * 100);
  const validMiscLines = miscLines.filter(
    (m) =>
      m.description.trim().length > 0 &&
      m.amount.trim() !== '' &&
      Number.isFinite(Number(m.amount)) &&
      Number(m.amount) >= 0,
  );
  // Stable signature of the misc rows that affect the live preview (description +
  // amount + uom + qty), so the debounced recompute re-fires on any misc edit. Kept
  // separate from validItems so the SKU deps below stay byte-identical to before.
  const miscPreviewSig = validMiscLines
    .map((m) => `${m.description}|${m.amount}|${m.uom}|${m.quantity}`)
    .join(';');
  // Gate the live preview on inventory being LOADED: toRequestLineItems resolves each
  // line's SF/bag coverage from skuById (built from inventoryData.skus). If the preview
  // fires before inventory loads (the reopen race), coverage is null → enteredSf/
  // coverageSfOverride are dropped → the server prices raw SF (huge inflation). Waiting
  // for inventory means the FIRST compute already carries coverage.
  const inventoryReady = (inventoryData?.skus?.length ?? 0) > 0;
  const canCalculate = customerId && validItems.length > 0 && inventoryReady;

  // An existing job (jobId) with an EMPTY draft can still "Save quote" — that
  // persists the wipe to $0 (the Clear-all use case). New jobs can't save empty.
  const isEmptyDraft = jobId != null && validItems.length === 0;
  // For the create button: need valid items + result, OR (jobId) an empty draft to wipe.
  const canCreate = jobId
    ? (validItems.length > 0 && result !== null) || isEmptyDraft
    : Boolean(customerId && address.trim() && validItems.length > 0 && result !== null);

  const doCalculate = useCallback(() => {
    if (!canCalculate) return;
    // SKU lines carry no `kind` from the pure builder (the back-compat preprocess
    // injects kind:'sku' server-side); tag them here so the discriminated-union
    // preview payload typechecks, then append the misc lines as kind:'misc'. Misc is
    // priced discount-exempt server-side and returned as its own result.miscellaneous
    // section — it never enters result.lineItems, so the materials rollup is unaffected.
    const skuLines = toRequestLineItems(validItems).map((li) => ({ ...li, kind: 'sku' as const }));
    const miscPreviewLines = validMiscLines.map((m) => ({
      kind: 'misc' as const,
      description: m.description.trim(),
      amountCents: dollarsToCents(m.amount),
      ...(m.uom ? { uom: m.uom } : {}),
      ...(m.quantity.trim() !== '' && Number.isFinite(Number(m.quantity))
        ? { quantity: Number(m.quantity) }
        : {}),
    }));
    pricingMutation.mutate({
      customerId,
      lineItems: [...skuLines, ...miscPreviewLines],
    });
    // skuById is LOAD-BEARING here, not noise: toRequestLineItems reads coverage from it,
    // so this memoized callback MUST be recreated when inventory loads — otherwise it
    // captures the pre-load empty map and the preview never recomputes (the reopen bug).
    // It changes identity only when inventory changes (stable across keystrokes).
    // miscPreviewSig re-creates it on any misc edit so the preview reflects misc live.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, skuById, validItems.length, miscPreviewSig, ...validItems.map((li) => li.skuId + li.quantity)]);

  // ALWAYS auto-recompute (debounced) on any line-item add/remove/edit. There is
  // no manual Calculate trigger or toggle. Draft only — never persists. When there
  // are no valid items (all rows blank / last SKU removed or blanked) clear the
  // preview so the readout empties; otherwise the stale table would linger.
  useEffect(() => {
    if (readOnly) return; // read-only renders from the frozen snapshot, no live preview
    if (!canCalculate) {
      setResult(null);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(doCalculate, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [readOnly, canCalculate, doCalculate]);

  // Clear success message after a few seconds
  useEffect(() => {
    if (!successMsg) return;
    const t = setTimeout(() => setSuccessMsg(''), 5000);
    return () => clearTimeout(t);
  }, [successMsg]);

  // The editable headline is the JOB SELL total = materials sell (post-discount)
  // + locked labor charge. No tax (this app is tax-free). Auto-syncs while the
  // user hasn't touched it; once touched, their override sticks.
  const laborChargeCents = job?.quoteLaborChargeCents ?? 0;
  useEffect(() => {
    if (overrideTouched) return;
    if (!result) {
      setOverrideDollars('');
    } else {
      const autoJobSellCents = result.totalCents + laborChargeCents;
      setOverrideDollars((autoJobSellCents / 100).toFixed(2));
    }
  }, [result, overrideTouched, laborChargeCents]);

  const addLineItem = () => {
    const key = nextKey++;
    setLineItems((prev) => [...prev, { key, skuId: '', quantity: '' }]);
    // Mark this brand-new row so its picker auto-focuses + opens on mount.
    setAutoOpenKey(key);
  };

  const removeLineItem = (key: number) => {
    const removed = lineItems.find((li) => li.key === key);
    setLineItems((prev) => {
      const next = prev.filter((li) => li.key !== key);
      return next.length === 0 ? [{ key: nextKey++, skuId: '', quantity: '' }] : next;
    });
    // Only invalidate the live preview if the removed row actually priced something
    // (had a SKU). Removing an empty / never-selected row leaves validItems unchanged,
    // so the auto-recalc effect won't re-run — nulling result here would blank the
    // table with no way to bring it back. The effect clears it when no valid items remain.
    if (removed?.skuId) setResult(null);
  };

  // Wipe the draft back to a single blank row + zero pricing. Draft-only — persists
  // nothing until "Save quote".
  const hasAnySku = lineItems.some((li) => li.skuId);
  // Clear-all is available when there's any SKU OR any non-empty misc row to wipe.
  const hasAnyMiscDraft = miscLines.some(
    (m) => m.description.trim() !== '' || m.amount.trim() !== '',
  );
  const clearAllLines = () => {
    setLineItems([{ key: nextKey++, skuId: '', quantity: '' }]);
    setMiscLines([]);
    setResult(null);
    setOverrideDollars('');
    setOverrideTouched(false);
  };

  const updateLineItem = (key: number, field: 'skuId' | 'quantity', value: string) => {
    setLineItems((prev) =>
      prev.map((li) =>
        li.key === key
          // Changing the row's SKU invalidates any seeded R-value (it belonged to the
          // previous SKU); preset picks carry their R in the composite skuId instead.
          ? { ...li, [field]: value, ...(field === 'skuId' ? { rValue: null } : {}) }
          : li,
      ),
    );
  };

  // --- Misc line handlers (draft-only until Save; the two-phase reconcile persists) ---
  const addMiscLine = () => {
    setMiscLines((prev) => [
      ...prev,
      { key: nextKey++, description: '', uom: '', quantity: '', amount: '' },
    ]);
  };
  const removeMiscLine = (key: number) => {
    setMiscLines((prev) => prev.filter((m) => m.key !== key));
  };
  // description / quantity / amount are all string fields — one generic setter.
  const updateMiscField = (
    key: number,
    field: 'description' | 'quantity' | 'amount',
    value: string,
  ) => {
    setMiscLines((prev) => prev.map((m) => (m.key === key ? { ...m, [field]: value } : m)));
  };
  // uom is the one non-string field (CanonicalUom | '') — its own typed setter.
  const updateMiscUom = (key: number, uom: CanonicalUom | '') => {
    setMiscLines((prev) => prev.map((m) => (m.key === key ? { ...m, uom } : m)));
  };

  // The headline input is the JOB SELL total. The server stores the MATERIALS
  // total in quote_amount_cents, so an override flows to materials only:
  // materialsSell = jobSell − lockedLaborCharge (labor never changes), clamped
  // at 0 (never negative). Costs are never touched. Returns the materials cents
  // to send, or null when the value matches the auto materials total (no override).
  const enteredJobSellCents = (() => {
    const trimmed = overrideDollars.trim();
    if (trimmed === '') return null;
    const num = Number(trimmed);
    if (!Number.isFinite(num) || num < 0) return null;
    return Math.round(num * 100);
  })();
  // True when the typed Job Sell is below the locked labor charge — materials
  // would go negative, so we clamp to $0 and surface a hint (don't touch labor).
  // Misc is a fixed, undiscounted addend on the job sell — the quote-amount override
  // flows to MATERIALS only (labor + misc are fixed). "Below" means the typed job sell
  // can't even cover labor + misc, so materials clamp to $0.
  const miscSellCents = result?.miscellaneous.sellCents ?? 0;
  const jobSellBelowLabor =
    overrideTouched &&
    enteredJobSellCents !== null &&
    enteredJobSellCents < laborChargeCents + miscSellCents;
  const computedOverrideCents = (() => {
    if (!overrideTouched || !result || enteredJobSellCents === null) return null;
    // Auto materials = discounted materials only (totalCents includes undiscounted misc).
    const autoMaterialsCents = result.totalCents - result.miscellaneous.sellCents;
    const materialsSellCents = Math.max(
      0,
      enteredJobSellCents - laborChargeCents - result.miscellaneous.sellCents,
    );
    if (materialsSellCents === autoMaterialsCents) return null; // equals auto → no override
    return materialsSellCents;
  })();

  // Post-save finalize (formerly createMutation.onSuccess): toast, reset the draft
  // (incl. misc), invalidate queries, notify the parent. Runs AFTER misc reconcile so
  // the dialog-closing onQuoteSaved fires only once misc has persisted.
  const finalizeAfterSave = (data: QuoteCreateResponse) => {
    // Toast shows the JOB total = materials (saved) + locked labor charge. No tax.
    const total = formatCents(data.quoteAmountCents + laborChargeCents);
    setSuccessMsg(`Job created! Quote: ${total}`);
    if (!jobId) {
      setCustomerId('');
      setAddress('');
      setPoNumber('');
      void queryClient.invalidateQueries({ queryKey: ['next-po'] });
    }
    setLineItems([{ key: nextKey++, skuId: '', quantity: '' }]);
    setMiscLines([]);
    setWasSeeded(false);
    setResult(null);
    setOverrideDollars('');
    setOverrideTouched(false);
    void queryClient.invalidateQueries({ queryKey: ['jobs'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard', 'today'] });
    if (jobId) {
      void queryClient.invalidateQueries({ queryKey: ['job', jobId] });
      // Insulation Remaining Pool reads ['job-phases'] — refresh so the pool reflects
      // the new BoM without a manual page refresh.
      void queryClient.invalidateQueries({ queryKey: ['job-phases', jobId] });
    }
    onQuoteSaved?.(data);
  };

  // Phase 2 of the save: reconcile the persisted misc lines to the current draft by
  // DIFFING against the seeded baseline (initialMiscLines) by id — add new rows, delete
  // removed/edited-old rows. Idempotent (re-running with the same draft is a no-op) and
  // partial-failure safe (a retry reconciles the remainder). NEVER a blind clear+re-add.
  // Touches ONLY job_misc_lines via the dedicated endpoints — the frozen SKU BoM in
  // field_estimates stays byte-identical. No-op fast path when nothing changed → the
  // common (no-misc) job makes zero extra API calls.
  const reconcileMisc = async (targetJobId: string) => {
    const baseline = new Map((initialMiscLines ?? []).map((m) => [m.id, m] as const));
    const keepIds = new Set<string>();
    const toCreate: MiscLineCreateInput[] = [];
    for (const m of validMiscLines) {
      const cents = dollarsToCents(m.amount);
      const base = m.id ? baseline.get(m.id) : undefined;
      const unchanged =
        base != null &&
        base.description === m.description.trim() &&
        base.amountCents === cents &&
        (base.uom ?? '') === m.uom &&
        (base.quantity != null ? String(base.quantity) : '') === m.quantity.trim();
      if (base && unchanged) {
        keepIds.add(m.id!);
      } else {
        toCreate.push({
          description: m.description.trim(),
          amountCents: cents,
          ...(m.uom ? { uom: m.uom } : {}),
          ...(m.quantity.trim() !== '' && Number.isFinite(Number(m.quantity))
            ? { quantity: Number(m.quantity) }
            : {}),
        });
      }
    }
    const toDelete = [...baseline.keys()].filter((id) => !keepIds.has(id));
    if (toCreate.length === 0 && toDelete.length === 0) return; // nothing to do
    setMiscSaving(true);
    try {
      // Deletes first (drop removed / edited-old rows), then creates the new set.
      for (const id of toDelete) await deleteMiscLine(targetJobId, id);
      for (const m of toCreate) await postMiscLine(targetJobId, m);
    } finally {
      setMiscSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!canCreate) return;
    setSuccessMsg('');
    // Empty draft on an existing job → persist the SKU wipe to $0 (Clear-all + Save).
    // Misc lives in its own table (untouched by the wipe) — reconcile it first so a
    // Clear-all that also cleared misc rows removes the persisted misc, and any misc
    // still present is preserved. Reconcile BEFORE the wipe closes the dialog.
    if (isEmptyDraft) {
      await reconcileMisc(jobId!);
      clearMutation.mutate();
      return;
    }
    if (!result) return;
    try {
      const data = await createMutation.mutateAsync({
        ...(jobId ? { jobId } : {}),
        customerId,
        // Address is only sent when creating a NEW job. When attaching to an existing
        // job (jobId set) the worker uses the job's own stored address — sending an
        // empty string here would fail validation. The input is hidden in jobId mode.
        ...(jobId ? {} : { address: address.trim() }),
        ...(poNumber.trim() ? { poNumber: poNumber.trim() } : {}),
        lineItems: toRequestLineItems(validItems),
        ...(computedOverrideCents !== null
          ? { overrideQuoteAmountCents: computedOverrideCents }
          : {}),
      });
      // Phase 2: reconcile misc on the resolved job id (existing jobId, or the freshly
      // created job's id). SKU-first ordering is required so a NEW job has an id first.
      await reconcileMisc(jobId ?? data.jobId);
      finalizeAfterSave(data);
    } catch {
      // createMutation.error / a misc endpoint error surfaces in the UI below; leave the
      // draft intact so the office can retry without losing rows.
    }
  };

  const selectedCustomer = customers?.find((c) => c.id === customerId);
  const createButtonText = jobId ? 'Save quote' : 'Create job';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calculator className="h-5 w-5 text-primary" />
          Pricing Calculator
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Customer selector — hidden when jobId is provided */}
        {!jobId && (
          <div>
            <label htmlFor="calc-customer" className="eyebrow mb-1.5 block">
              Customer
            </label>
            <CustomerSearchSelect
              id="calc-customer"
              value={customerId}
              customers={customers ?? []}
              onChange={(cid) => {
                setCustomerId(cid);
                setResult(null);
              }}
              placeholder="Choose a customer…"
            />
            {selectedCustomer && selectedCustomer.discountPct > 0 && (
              <p className="mt-1 text-xs text-primary">
                {selectedCustomer.discountPct}% discount will be applied
              </p>
            )}
          </div>
        )}

        {/* Address + Email — hidden when jobId is provided */}
        {!jobId && (
          <>
            <div className="grid grid-cols-[1fr_auto] gap-3">
              <div>
                <label htmlFor="calc-address" className="eyebrow mb-1.5 block">
                  Address *
                </label>
                <Input
                  id="calc-address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="123 Oak St, Anywhere, USA"
                />
              </div>
              <div>
                <label htmlFor="calc-po" className="eyebrow mb-1.5 block">
                  PO#
                </label>
                <Input
                  id="calc-po"
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                  placeholder={
                    nextPoData ? `Auto (${nextPoData.nextPoNumber})` : 'Auto'
                  }
                  className="w-24 font-mono"
                />
              </div>
            </div>
          </>
        )}

        {/* Line items editor — hidden in read-only "View Profitability" mode. */}
        {!readOnly && (
        <div>
          <div className="mb-1.5 flex items-baseline justify-between gap-2">
            <label className="eyebrow">Line items</label>
            {wasSeeded && (
              <span className="text-xs text-primary">
                Pre-filled from field estimates — review and adjust
              </span>
            )}
          </div>
          <div className="space-y-2">
            {lineItems.map((li) => {
              // Batting line → quantity field means SF; show implied whole bags.
              const cov = lineCoverageSf(li.skuId);
              const isSf = cov != null;
              const qtyNum = Number(li.quantity);
              const bags =
                isSf && qtyNum > 0 ? bagsFromSf(qtyNum, cov) : null;
              return (
              <div key={li.key} className="flex items-center gap-2">
                <SkuSearchSelect
                  id={`li-sku-${li.key}`}
                  value={li.skuId}
                  skus={skus}
                  categories={categories}
                  productLine={job?.productLine ?? null}
                  onChange={(skuId) => updateLineItem(li.key, 'skuId', skuId)}
                  autoOpen={li.key === autoOpenKey}
                />
                <Input
                  type="number"
                  min="0"
                  step="any"
                  placeholder={isSf ? 'SF' : 'Qty'}
                  value={li.quantity}
                  onChange={(e) => updateLineItem(li.key, 'quantity', e.target.value)}
                  className="h-9 w-24 shrink-0"
                />
                {/* Fixed-width unit label — ALWAYS rendered (even when empty)
                    so every row has identical layout and the SKU dropdown
                    gets the same flex-1 space regardless of selection state.
                    Batting shows "SF" (input unit) + an implied-bags hint. */}
                <span className="w-16 shrink-0 text-right text-xs text-taupe">
                  {isSf
                    ? bags != null
                      ? `SF → ${bags} bag${bags === 1 ? '' : 's'}`
                      : 'SF'
                    : (skus.find((s) => s.skuId === li.skuId)?.unit ?? '')}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => removeLineItem(li.key)}
                  className="h-9 w-9 shrink-0 p-0 text-muted-foreground hover:border-destructive hover:text-destructive"
                  disabled={lineItems.length === 1 && !li.skuId}
                  aria-label="Remove this line item"
                  title="Remove this line item"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
              );
            })}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={addLineItem}>
              <Plus className="h-3.5 w-3.5" />
              Add item
            </Button>
            {/* Draft-only clear, gated by a confirm dialog. Persists nothing until
                "Save quote". (The persisted "Clear estimate" control was removed.) */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setClearConfirmOpen(true)}
              disabled={!hasAnySku && !hasAnyMiscDraft}
              className="text-muted-foreground hover:text-destructive"
              title="Remove all SKUs and misc lines from this draft"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Clear all
            </Button>
          </div>
        </div>
        )}

        {/* Miscellaneous lines editor — free-form write-in lines (own quote category,
            discount-EXEMPT, no markup). A SEPARATE list from the SKU line items above so
            the SKU BoM / materials rollup is structurally unaffected. Hidden in read-only
            mode (the frozen "View Profitability" view renders misc from the snapshot). */}
        {!readOnly && (
          <div>
            <label className="eyebrow mb-1.5 block">Miscellaneous</label>
            {miscLines.length > 0 && (
              <div className="space-y-2">
                {miscLines.map((m) => (
                  <div key={m.key} className="flex items-center gap-2">
                    <Input
                      placeholder="Description (e.g. Custom fascia repair)"
                      value={m.description}
                      onChange={(e) => updateMiscField(m.key, 'description', e.target.value)}
                      className="h-9 flex-1"
                    />
                    <select
                      value={m.uom}
                      onChange={(e) => updateMiscUom(m.key, e.target.value as CanonicalUom | '')}
                      className="h-9 w-[4.5rem] shrink-0 rounded-md border border-input bg-background px-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      aria-label="Unit (optional)"
                    >
                      <option value="">—</option>
                      {CANONICAL_UOMS.map((u) => (
                        <option key={u} value={u}>
                          {u}
                        </option>
                      ))}
                    </select>
                    <Input
                      type="number"
                      min="0"
                      step="any"
                      placeholder="Qty"
                      value={m.quantity}
                      onChange={(e) => updateMiscField(m.key, 'quantity', e.target.value)}
                      className="h-9 w-16 shrink-0"
                      aria-label="Quantity (optional)"
                    />
                    <div className="relative w-28 shrink-0">
                      <span className="pointer-events-none absolute inset-y-0 left-2 flex items-center text-sm text-muted-foreground">
                        $
                      </span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        placeholder="0.00"
                        value={m.amount}
                        onChange={(e) => updateMiscField(m.key, 'amount', e.target.value)}
                        className="tnum h-9 w-full pl-5 text-right"
                        aria-label="Amount"
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => removeMiscLine(m.key)}
                      className="h-9 w-9 shrink-0 p-0 text-muted-foreground hover:border-destructive hover:text-destructive"
                      aria-label="Remove this misc line"
                      title="Remove this misc line"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-2">
              <Button size="sm" variant="outline" onClick={addMiscLine}>
                <Plus className="h-3.5 w-3.5" />
                Add misc line
              </Button>
            </div>
            <p className="mt-1.5 text-xs text-taupe">
              Flat write-in amount — not discounted, no markup. Shown as its own
              “Miscellaneous” quote category.
            </p>
          </div>
        )}

        {/* No Calculate button / Auto-calculate toggle — pricing always recomputes
            automatically on add/remove/edit (draft only; persists nothing). */}

        {/* Error */}
        {pricingMutation.error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {pricingMutation.error instanceof Error
              ? pricingMutation.error.message
              : 'Pricing failed'}
          </div>
        )}

        {/* Results (live editable preview) */}
        {!readOnly && result && (
          <div className="rounded-md border border-border bg-secondary/30">
            {/* Per-line breakdown — shared COST / SELL / MARGIN grid (READOUT_GRID),
                per consumption UOM. Margin is now its own column (right-aligned with
                the Profitability MARGIN below); the pill keeps the margin too. */}
            <div className="divide-y divide-border">
              {/* Column header */}
              <div className={`${READOUT_GRID} px-4 py-2 text-[0.6875rem] uppercase tracking-wide text-taupe`}>
                <div className="min-w-0">Item</div>
                <div className="text-right">Cost</div>
                <div className="text-right">Sell</div>
                <div className="text-right">Margin</div>
              </div>
              {result.lineItems.map((li, i) => {
                // Shared-bag preset SKUs sell per-R-value $/SF but are quoted/charged in
                // whole BAGS (quantity = bags). ALL insulation lines (KN3646 presets AND
                // normal batts like B09E/B22EV) carry consumptionUom='SF' — so label every
                // insulation line by its PURCHASE unit (BAG) so the table reads "N BAG @
                // $/BAG", never "N SF @ $/SF" or "N ea". Non-insulation → consumptionUom||unit.
                const uom = insulationUnitByCode.get(li.skuCode) ?? (li.consumptionUom || li.unit);
                const unitCostCents = Math.round(li.costPerConsumptionCents);
                const extCostCents = Math.round(li.costPerConsumptionCents * li.quantity);
                return (
                  <div key={i} className={`${READOUT_GRID} px-4 py-3`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-foreground">
                        {li.skuName}
                        {li.variant && (
                          <span className="font-normal text-muted-foreground">
                            {' · '}
                            {li.variant}
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-taupe">
                        {/* Detail line: SKU code · qty · per-unit. Margin pill removed —
                            the MARGIN column shows each item's margin now. */}
                        <span className="font-mono">{li.skuCode}</span>
                        <span>·</span>
                        <span>
                          {li.quantity.toLocaleString()} {uom}
                        </span>
                      </div>
                    </div>
                    {/* Cost column */}
                    <div className="tnum text-right text-sm">
                      <div className="font-semibold text-foreground">{formatCents(extCostCents)}</div>
                      <div className="text-xs text-taupe">
                        {formatCents(unitCostCents)}/{uom}
                      </div>
                    </div>
                    {/* Sell column */}
                    <div className="tnum text-right text-sm">
                      <div className="font-semibold text-foreground">{formatCents(li.linePriceCents)}</div>
                      <div className="text-xs text-taupe">
                        {formatCents(li.unitPriceCents)}/{uom}
                      </div>
                    </div>
                    {/* Margin column */}
                    <div className="tnum text-right text-sm text-foreground">
                      {formatMarginPct(unitCostCents, li.unitPriceCents)}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Subtotal row removed — the materials total is the Materials row in
                the Profitability section below (which nets any customer discount). */}

            {/* Margin summary — COST · SELL · MARGIN columns for
                Materials, Labor, Job total. The Job SELL total is the big
                editable headline (bound to quoteTotalCents). NO TAX: job sell =
                materials(post-discount) + labor charge. An override changes
                materials sell only (labor charge fixed; costs never change);
                below-labor clamps materials to $0. % suppress to "—" when cost ≤ 0. */}
            {(() => {
              const matCost = result.lineItems.reduce(
                (acc, li) => acc + Math.round(li.costPerConsumptionCents * li.quantity),
                0,
              );
              const hasLabor = !!jobId && !!job;
              const laborCharge = hasLabor ? job!.quoteLaborChargeCents : 0;
              const laborCost = hasLabor ? job!.quoteLaborCostCents : 0;
              // Misc = discount-exempt, fixed addend (cost 0). totalCents already includes it.
              const miscSell = result.miscellaneous.sellCents;
              const autoJobSell = result.totalCents + laborCharge; // materials(disc) + misc + labor
              // Effective Job Sell = the typed headline (if touched + valid) else auto.
              const jobSell =
                overrideTouched && enteredJobSellCents !== null ? enteredJobSellCents : autoJobSell;
              // Override flows to MATERIALS only; misc + labor are fixed. Clamp at 0.
              const matSell = Math.max(0, jobSell - laborCharge - miscSell);
              const jobCost = matCost + laborCost; // misc cost = 0
              // 3 numeric cells (no MARKUP), right-aligned on the shared grid edges.
              const ProfitCells = ({ cost, sell }: { cost: number; sell: number }) => (
                <>
                  <span className="tnum text-right">{formatCents(cost)}</span>
                  <span className="tnum text-right">{formatCents(sell)}</span>
                  <span className="tnum text-right">{formatMarginPct(cost, sell)}</span>
                </>
              );
              return (
                <div className="border-t border-border px-4 py-3 space-y-2">
                  {/* Section label only — no column headers; values align beneath the
                      Item section's COST/SELL/MARGIN headers via the shared grid. */}
                  <div className="text-[0.6875rem] uppercase tracking-wide text-taupe">
                    Profitability
                  </div>
                  <div className={`${READOUT_GRID} text-sm text-muted-foreground`}>
                    <span>Materials</span>
                    <ProfitCells cost={matCost} sell={matSell} />
                  </div>
                  {/* Miscellaneous — discount-exempt, cost $0 / margin 100%. Its own row
                      so it's never folded into the materials figure. */}
                  {result.miscellaneous.lines.length > 0 && (
                    <div className={`${READOUT_GRID} text-sm text-muted-foreground`}>
                      <span>Miscellaneous</span>
                      <span className="tnum text-right">{formatCents(0)}</span>
                      <span className="tnum text-right">{formatCents(miscSell)}</span>
                      <span className="tnum text-right">100%</span>
                    </div>
                  )}
                  {hasLabor && (
                    <div className={`${READOUT_GRID} text-sm text-muted-foreground`}>
                      <span>Labor</span>
                      <ProfitCells cost={laborCost} sell={laborCharge} />
                    </div>
                  )}
                  {/* Job total — editable Job Sell sits IN the SELL column so its
                      value right-aligns to the shared SELL edge (not off-grid). */}
                  <div className="border-t border-border pt-2">
                    <div className={`${READOUT_GRID} items-center text-sm font-semibold text-foreground`}>
                      <div className="flex items-center gap-1.5">
                        <span className="text-base">Job total</span>
                        {/* Reset-to-auto control only when overridden; no "auto" tag otherwise. */}
                        {overrideTouched && computedOverrideCents !== null && (
                          <button
                            type="button"
                            onClick={() => setOverrideTouched(false)}
                            className="text-[0.6875rem] font-normal text-primary underline-offset-2 hover:underline"
                            title={`Reset to auto (${formatCents(autoJobSell)})`}
                          >
                            override · reset
                          </button>
                        )}
                      </div>
                      <span className="tnum text-right">{formatCents(jobCost)}</span>
                      {/* editable Job Sell total (bound to quoteTotalCents). $ overlays
                          at the left; pr-0 + -mr-px pull the value flush to the SELL
                          column edge (counter the input's right padding + 1px border)
                          so it right-aligns with the static SELL values above. */}
                      <div className="relative">
                        <span className="pointer-events-none absolute inset-y-0 left-1 flex items-center text-sm font-bold">
                          $
                        </span>
                        <Input
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min="0"
                          value={overrideDollars}
                          onChange={(e) => {
                            setOverrideDollars(e.target.value);
                            setOverrideTouched(true);
                          }}
                          className="tnum h-8 w-full pl-4 pr-0 -mr-px text-right text-base font-bold"
                          aria-label="Job sell total"
                        />
                      </div>
                      <span className="tnum text-right">{formatMarginPct(jobCost, jobSell)}</span>
                    </div>
                    {jobSellBelowLabor && (
                      <p className="mt-1.5 text-xs text-destructive">
                        Job Sell total is below locked labor{miscSell > 0 ? ' + misc' : ''} (
                        {formatCents(laborCharge + miscSell)}). Materials sell is clamped to $0 —
                        raise the total or adjust labor in Settings; labor{miscSell > 0 ? ' + misc' : ''} aren’t
                        changed here.
                      </p>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Footer (editable mode) — Save quote + Cancel. Kept OUTSIDE the results
            block so clearing all line items (which sets result=null) doesn't drop
            the action buttons along with the table. Save stays disabled until the
            draft has valid items. */}
        {!readOnly && (
          <div className="rounded-md border border-border bg-secondary/30 px-4 py-3 space-y-2">
            <div className="flex gap-2">
              <Button
                className="flex-1"
                disabled={!canCreate || createMutation.isPending || clearMutation.isPending || miscSaving}
                onClick={handleCreate}
              >
                {createMutation.isPending || clearMutation.isPending || miscSaving ? (
                  'Saving…'
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    {isEmptyDraft ? 'Save empty quote' : createButtonText}
                  </>
                )}
              </Button>
              {onCancel && (
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={onCancel}
                  disabled={createMutation.isPending}
                >
                  Cancel
                </Button>
              )}
            </div>
            {createMutation.error && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                {createMutation.error instanceof Error
                  ? createMutation.error.message
                  : 'Quote creation failed'}
              </div>
            )}
          </div>
        )}

        {/* Results (read-only "View Profitability") — rendered ENTIRELY from the
            frozen quote snapshot (job.fieldEstimates + the snapshot totals), never
            the live preview. Same READOUT_GRID alignment as the editable view. */}
        {readOnly && job && (() => {
          const roLines = job.fieldEstimates.filter(
            (e) => e.skuId && e.status === 'confirmed' && e.context === 'quote',
          );
          // Per-line + materials cost from the frozen per-consumption cost.
          const matCost = roLines.reduce(
            (acc, e) => acc + Math.round((e.costCentsAtEstimate ?? 0) * e.quantity),
            0,
          );
          const matSell = job.quoteAmountCents ?? 0; // frozen materials total
          const laborCharge = job.quoteLaborChargeCents;
          const laborCost = job.quoteLaborCostCents;
          const jobCost = matCost + laborCost;
          const jobSell = job.quoteTotalCents; // = materials + labor (tax-free)
          const margin = (cost: number, sell: number) =>
            cost > 0 ? formatMarginPct(cost, sell) : '—';
          const hasLabor = laborCharge > 0 || laborCost > 0;
          return (
            <div className="rounded-md border border-border bg-secondary/30">
              {/* Item table (frozen) */}
              <div className="divide-y divide-border">
                <div className={`${READOUT_GRID} px-4 py-2 text-[0.6875rem] uppercase tracking-wide text-taupe`}>
                  <div className="min-w-0">Item</div>
                  <div className="text-right">Cost</div>
                  <div className="text-right">Sell</div>
                  <div className="text-right">Margin</div>
                </div>
                {roLines.map((e) => {
                  // ALL insulation lines bill in whole BAGS though their SKU's
                  // consumptionUom is 'SF' — label by the PURCHASE unit (BAG), not "$/SF"
                  // or "ea". Mirrors the live table above (keyed by code via the batting
                  // map; falls through for non-insulation / inactive SKUs).
                  const uom =
                    (e.skuCode && insulationUnitByCode.get(e.skuCode)) ||
                    e.consumptionUom ||
                    e.unit ||
                    '';
                  const unitCost = e.costCentsAtEstimate ?? 0;
                  const extCost = Math.round(unitCost * e.quantity);
                  const extSell = Math.round(e.priceCentsAtEstimate * e.quantity);
                  return (
                    <div key={e.id} className={`${READOUT_GRID} px-4 py-3`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground">
                          {e.skuName}
                          {e.variant && (
                            <span className="font-normal text-muted-foreground">
                              {' · '}
                              {e.variant}
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-taupe">
                          <span className="font-mono">{e.skuCode}</span>
                          <span>·</span>
                          <span>
                            {e.quantity.toLocaleString()} {uom}
                          </span>
                        </div>
                      </div>
                      <div className="tnum text-right text-sm">
                        <div className="font-semibold text-foreground">{formatCents(extCost)}</div>
                        <div className="text-xs text-taupe">{formatCents(unitCost)}/{uom}</div>
                      </div>
                      <div className="tnum text-right text-sm">
                        <div className="font-semibold text-foreground">{formatCents(extSell)}</div>
                        <div className="text-xs text-taupe">{formatCents(e.priceCentsAtEstimate)}/{uom}</div>
                      </div>
                      <div className="tnum text-right text-sm text-foreground">
                        {margin(unitCost, e.priceCentsAtEstimate)}
                      </div>
                    </div>
                  );
                })}
                {/* Frozen misc lines (migration 0075) — own category, cost $0 / 100% margin. */}
                {job.miscLines.map((m) => (
                  <div key={m.id} className={`${READOUT_GRID} px-4 py-3`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-foreground">{m.description}</div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-taupe">
                        <span>Miscellaneous</span>
                        {m.quantity != null && (
                          <>
                            <span>·</span>
                            <span>
                              {m.quantity.toLocaleString()}
                              {m.uom ? ` ${m.uom}` : ''}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="tnum text-right text-sm font-semibold text-foreground">
                      {formatCents(0)}
                    </div>
                    <div className="tnum text-right text-sm font-semibold text-foreground">
                      {formatCents(m.amountCents)}
                    </div>
                    <div className="tnum text-right text-sm text-foreground">100%</div>
                  </div>
                ))}
              </div>

              {/* Profitability (frozen) — Job Sell is STATIC text, no input. */}
              <div className="border-t border-border px-4 py-3 space-y-2">
                <div className="text-[0.6875rem] uppercase tracking-wide text-taupe">Profitability</div>
                <div className={`${READOUT_GRID} text-sm text-muted-foreground`}>
                  <span>Materials</span>
                  <span className="tnum text-right">{formatCents(matCost)}</span>
                  <span className="tnum text-right">{formatCents(matSell)}</span>
                  <span className="tnum text-right">{margin(matCost, matSell)}</span>
                </div>
                {job.miscLines.length > 0 && (
                  <div className={`${READOUT_GRID} text-sm text-muted-foreground`}>
                    <span>Miscellaneous</span>
                    <span className="tnum text-right">{formatCents(0)}</span>
                    <span className="tnum text-right">{formatCents(job.miscTotalCents)}</span>
                    <span className="tnum text-right">100%</span>
                  </div>
                )}
                {hasLabor && (
                  <div className={`${READOUT_GRID} text-sm text-muted-foreground`}>
                    <span>Labor</span>
                    <span className="tnum text-right">{formatCents(laborCost)}</span>
                    <span className="tnum text-right">{formatCents(laborCharge)}</span>
                    <span className="tnum text-right">{margin(laborCost, laborCharge)}</span>
                  </div>
                )}
                <div className="border-t border-border pt-2">
                  <div className={`${READOUT_GRID} items-baseline text-sm font-semibold text-foreground`}>
                    <span className="text-base">Job total</span>
                    <span className="tnum text-right">{formatCents(jobCost)}</span>
                    <span className="tnum text-right text-base">{formatCents(jobSell)}</span>
                    <span className="tnum text-right">{margin(jobCost, jobSell)}</span>
                  </div>
                </div>
              </div>

              {/* Footer — Close only (read-only never saves). */}
              {onCancel && (
                <div className="border-t border-border px-4 py-3">
                  <Button variant="outline" className="w-full" onClick={onCancel}>
                    Close
                  </Button>
                </div>
              )}
            </div>
          );
        })()}

        {/* Success message */}
        {successMsg && (
          <div className="flex items-center gap-2 rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-sm text-primary">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            {successMsg}
          </div>
        )}

        {/* "Clear all" confirmation — draft only, persists nothing. Clears the
            working rows only on confirm; Cancel leaves the draft untouched. */}
        <Dialog open={clearConfirmOpen} onOpenChange={setClearConfirmOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Clear all items?</DialogTitle>
              <DialogDescription>
                This removes all SKUs and misc lines from the working draft. Nothing is
                saved until you click “Save quote”.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setClearConfirmOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  clearAllLines();
                  setClearConfirmOpen(false);
                }}
              >
                <Trash2 className="h-4 w-4" />
                Clear all
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
